/**
 * Department Details Page - Microsoft 365 Style
 * Displays comprehensive department information and employees
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { makeStyles, Button, Text, Avatar, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, EditRegular, BuildingRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { ApiClient, newToast } from '../../configurations/index.config';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  departmentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '24px',
    borderBottom: '1px solid #e1dfdd',
  },
  departmentIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '12px',
    backgroundColor: '#008272',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '32px',
  },
  departmentName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  departmentDescription: {
    fontSize: '14px',
    color: '#605e5c',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    padding: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: '14px',
    color: '#323130',
  },
  employeesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  employeeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '6px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #e1dfdd',
  },
  employeeName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  employeeRole: {
    fontSize: '12px',
    color: '#605e5c',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  fullWidthSection: {
    gridColumn: '1 / -1',
  },
});

interface Department {
  id: string;
  department_name: string;
  description?: string;
  head_of_department?: string;
  head_name?: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role_name?: string;
  department_name?: string;
}

export const DepartmentDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [department, setDepartment] = useState<Department | null>(
    location.state?.department || null
  );
  const [departmentHead, setDepartmentHead] = useState<Employee | null>(null);
  const [departmentEmployees, setDepartmentEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(!location.state?.department);

  const fetchDepartmentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-departments');
      if (response.data.status === 'success') {
        const departments = response.data.data || [];
        const foundDepartment = departments.find((dept: Department) => dept.id === id);

        if (foundDepartment) {
          setDepartment(foundDepartment);
        } else {
          newToast('error', 'Department not found');
          navigate('/account/departments');
        }
      } else {
        newToast('error', response.data?.message || 'Failed to fetch department details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [id, navigate]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await ApiClient.get('/get-employee-data');
      if (response.data.status === 'success') {
        const employees = response.data.data || [];

        // Filter employees by department
        const deptEmployees = employees.filter(
          (emp: Employee) => emp.department_name === department?.department_name
        );
        setDepartmentEmployees(deptEmployees);

        // Find department head
        if (department?.head_of_department) {
          const head = employees.find((emp: Employee) => emp.id === department.head_of_department);
          setDepartmentHead(head || null);
        }
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  }, [department]);

  useEffect(() => {
    if (!department && id) {
      fetchDepartmentDetails();
    }
  }, [department, id, fetchDepartmentDetails]);

  useEffect(() => {
    if (department) {
      fetchEmployees();
    }
  }, [department, fetchEmployees]);

  const handleBack = () => {
    navigate('/account/departments');
  };

  const handleEdit = () => {
    if (department) {
      navigate('/account/departments', { state: { editDepartment: department } });
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading department details..." />
        </div>
      </div>
    );
  }

  if (!department) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Department not found
          </Text>
          <Button appearance="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
            Back to Departments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Department Details</Text>
        </div>
        <div className={styles.headerActions}>
          <Button icon={<EditRegular />} appearance="primary" onClick={handleEdit}>
            Edit Department
          </Button>
        </div>
      </div>

      {/* Department Card */}
      <ContentCard>
        {/* Department Header */}
        <div className={styles.departmentHeader}>
          <div className={styles.departmentIcon}>
            <BuildingRegular />
          </div>
          <div>
            <Text className={styles.departmentName}>{department.department_name}</Text>
            {department.description && (
              <Text className={styles.departmentDescription}>{department.description}</Text>
            )}
          </div>
        </div>

        {/* Department Details Grid */}
        <div className={styles.contentGrid}>
          {/* Department Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Department Information</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Department ID</Text>
                <Text className={styles.fieldValue}>{department.id}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Department Name</Text>
                <Text className={styles.fieldValue}>{department.department_name}</Text>
              </div>
              {department.description && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Description</Text>
                  <Text className={styles.fieldValue}>{department.description}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Department Head */}
          {departmentHead && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Department Head</Text>
              <div className={styles.fieldGroup}>
                <div className={styles.field}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                    }}
                  >
                    <Avatar
                      name={`${departmentHead.first_name} ${departmentHead.last_name}`}
                      color="brand"
                      size={48}
                    />
                    <div>
                      <Text className={styles.fieldValue} style={{ fontWeight: '600' }}>
                        {departmentHead.first_name} {departmentHead.last_name}
                      </Text>
                      {departmentHead.role_name && (
                        <Text style={{ fontSize: '12px', color: '#605e5c', display: 'block' }}>
                          {departmentHead.role_name}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Email</Text>
                  <Text className={styles.fieldValue}>
                    <a
                      href={`mailto:${departmentHead.email}`}
                      style={{ color: '#0078d4', textDecoration: 'none' }}
                    >
                      {departmentHead.email}
                    </a>
                  </Text>
                </div>
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Phone</Text>
                  <Text className={styles.fieldValue}>
                    <a
                      href={`tel:${departmentHead.phone}`}
                      style={{ color: '#0078d4', textDecoration: 'none' }}
                    >
                      {departmentHead.phone}
                    </a>
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Employees in Department */}
          <div className={`${styles.section} ${styles.fullWidthSection}`}>
            <Text className={styles.sectionTitle}>Employees ({departmentEmployees.length})</Text>
            {departmentEmployees.length === 0 ? (
              <Text style={{ color: '#605e5c', fontSize: '14px' }}>
                No employees assigned to this department
              </Text>
            ) : (
              <div className={styles.employeesList}>
                {departmentEmployees.map(employee => (
                  <div key={employee.id} className={styles.employeeItem}>
                    <Avatar
                      name={`${employee.first_name} ${employee.last_name}`}
                      color="colorful"
                      size={32}
                    />
                    <div style={{ flex: 1 }}>
                      <Text className={styles.employeeName}>
                        {employee.first_name} {employee.last_name}
                      </Text>
                      {employee.role_name && (
                        <Text className={styles.employeeRole}>{employee.role_name}</Text>
                      )}
                    </div>
                    <Button
                      size="small"
                      appearance="subtle"
                      onClick={() => navigate(`/account/employee-details/${employee.id}`)}
                    >
                      View Profile
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default DepartmentDetailsFluentUI;
